import React from 'react';

export interface Template11ResumeData {
  Name: string;
  JobTitle?: string;
  Phone?: string;
  Email?: string;
  LinkedIn?: string;
  City?: string;
  State?: string;
  Summary?: string;
  Skills: Array<{ Skill: string; Level?: number }>;
  Education: Array<{
    Degree: string;
    Institution: string;
    StartDate?: string;
    EndDate?: string;
  }>;
  ProfessionalExperience: Array<{
    JobTitle: string;
    Company: string;
    StartDate?: string;
    EndDate?: string;
    Responsibilities: string[];
  }>;
  KeyProjects?: Array<{
    ProjectName: string;
    Company?: string;
    Details: string[];
  }>;
}

interface Template11Props {
  data: Template11ResumeData;
}

export const Template11: React.FC<Template11Props> = ({ data }) => {
  // Convert Template1 data format to Template11 format if needed
  const convertedData = {
    ...data,
    ProfessionalExperience: data.ProfessionalExperience?.map((exp: any) => ({
      JobTitle: exp.JobTitle || exp.job_title || '',
      Company: exp.Company || exp.company || '',
      StartDate: exp.StartDate || exp.start_date || '',
      EndDate: exp.EndDate || exp.end_date || '',
      Responsibilities: exp.Responsibilities || exp.Descriptions?.map((d: any) => d.Description || d) || []
    })) || [],
    Education: data.Education?.map((edu: any) => ({
      Degree: edu.Degree || edu.College_Degree_Name || '',
      Institution: edu.Institution || edu.College_Name || '',
      StartDate: edu.StartDate || '',
      EndDate: edu.EndDate || edu.Year_Earned || ''
    })) || [],
    Skills: data.Skills || []
  };

  // Format date for display
  const formatDateRange = (startDate?: string, endDate?: string) => {
    if (!startDate) return '';
    const end = endDate || 'Present';
    return `${startDate} - ${end}`;
  };

  // Get location string
  const getLocation = () => {
    if (convertedData.City && convertedData.State) {
      return `${convertedData.City}, ${convertedData.State}`;
    }
    return convertedData.City || convertedData.State || '';
  };

  return (
    <div className="w-full bg-[#e9ecef] font-sans text-[#2d3748] antialiased">
      <div className="max-w-[900px] mx-auto my-8 bg-white shadow-lg flex rounded-lg overflow-hidden">
        {/* Left Column / Sidebar */}
        <aside className="bg-[#f8f9fa] w-[35%] p-8">
          {/* Profile Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl mb-1 font-semibold text-[#2d3748]">
              {convertedData.Name}
            </h1>
            <h2 className="text-lg font-normal text-[#e67e22] border-t border-b border-[#e2e8f0] py-2">
              {convertedData.JobTitle || convertedData.ProfessionalExperience?.[0]?.JobTitle || ''}
            </h2>
          </div>

          {/* Contact Section */}
          <div className="mb-6">
            <h3 className="text-base font-semibold uppercase tracking-wide text-[#2d3748] mb-4 pb-1 border-b-2 border-[#e67e22]">
              Contact
            </h3>
            <ul className="list-none">
              {convertedData.Phone && (
                <li className="flex items-center mb-3 text-sm">
                  <svg className="w-5 h-5 mr-3 flex-shrink-0 fill-[#718096]" viewBox="0 0 24 24">
                    <path d="M6.62,10.79C8.06,13.62 10.38,15.94 13.21,17.38L15.41,15.18C15.69,14.9 16.08,14.82 16.43,14.93C17.55,15.3 18.75,15.5 20,15.5A1,1 0 0,1 21,16.5V20A1,1 0 0,1 20,21A17,17 0 0,1 3,4A1,1 0 0,1 4,3H7.5A1,1 0 0,1 8.5,4C8.5,5.25 8.7,6.45 9.07,7.57C9.18,7.92 9.1,8.31 8.82,8.59L6.62,10.79Z"></path>
                  </svg>
                  <span>{convertedData.Phone}</span>
                </li>
              )}
              {convertedData.Email && (
                <li className="flex items-center mb-3 text-sm">
                  <svg className="w-5 h-5 mr-3 flex-shrink-0 fill-[#718096]" viewBox="0 0 24 24">
                    <path d="M20,8L12,13L4,8V6L12,11L20,6M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.1,4 20,4Z"></path>
                  </svg>
                  <a 
                    href={`mailto:${convertedData.Email}`}
                    className="text-[#2d3748] break-all transition-colors hover:text-[#e67e22]"
                  >
                    {convertedData.Email}
                  </a>
                </li>
              )}
              {convertedData.LinkedIn && (
                <li className="flex items-center mb-3 text-sm">
                  <svg className="w-5 h-5 mr-3 flex-shrink-0 fill-[#718096]" viewBox="0 0 24 24">
                    <path d="M19,3A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5A2,2 0 0,1 3,19V5A2,2 0 0,1 5,3H19M18.5,18.5V13.2A3.26,3.26 0 0,0 15.24,9.94C14.39,9.94 13.4,10.43 12.9,11.2V10.13H10.13V18.5H12.9V13.57C12.9,12.8 13.5,12.17 14.31,12.17A1.4,1.4 0 0,1 15.71,13.57V18.5H18.5M6.88,8.56A1.68,1.68 0 0,0 8.56,6.88C8.56,6 7.81,5.31 6.88,5.31A1.69,1.69 0 0,0 5.19,6.88C5.19,7.81 5.94,8.56 6.88,8.56M8.27,18.5V10.13H5.5V18.5H8.27Z"></path>
                  </svg>
                  <a 
                    href={convertedData.LinkedIn.startsWith('http') ? convertedData.LinkedIn : `https://${convertedData.LinkedIn}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#2d3748] break-all transition-colors hover:text-[#e67e22]"
                  >
                    {convertedData.LinkedIn.replace(/^https?:\/\//, '')}
                  </a>
                </li>
              )}
              {getLocation() && (
                <li className="flex items-center mb-3 text-sm">
                  <svg className="w-5 h-5 mr-3 flex-shrink-0 fill-[#718096]" viewBox="0 0 24 24">
                    <path d="M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z"></path>
                  </svg>
                  <span>{getLocation()}</span>
                </li>
              )}
            </ul>
          </div>

          {/* Skills Section */}
          {convertedData.Skills && convertedData.Skills.length > 0 && (
            <div className="mb-6">
              <h3 className="text-base font-semibold uppercase tracking-wide text-[#2d3748] mb-4 pb-1 border-b-2 border-[#e67e22]">
                Skills
              </h3>
              <ul className="list-none">
                {convertedData.Skills.map((skill, index) => {
                  const level = skill.Level || 85; // Default to 85% if no level provided
                  return (
                    <li key={index} className="mb-4 text-sm">
                      <span className="block mb-1 font-medium">{skill.Skill}</span>
                      <div className="bg-[#dcdcdc] h-2 rounded overflow-hidden">
                        <div 
                          className="bg-[#e67e22] h-full rounded"
                          style={{ width: `${level}%` }}
                        ></div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Education Section */}
          {convertedData.Education && convertedData.Education.length > 0 && (
            <div className="mb-6">
              <h3 className="text-base font-semibold uppercase tracking-wide text-[#2d3748] mb-4 pb-1 border-b-2 border-[#e67e22]">
                Education
              </h3>
              {convertedData.Education.map((edu, index) => (
                <div key={index} className="mb-4">
                  <h4 className="text-base font-semibold mb-0.5">{edu.Degree}</h4>
                  <p className="text-sm text-[#718096]">{edu.Institution}</p>
                  {(edu.StartDate || edu.EndDate) && (
                    <p className="text-sm text-[#718096]">
                      {formatDateRange(edu.StartDate, edu.EndDate)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </aside>

        {/* Right Column / Main Content */}
        <main className="w-[65%] p-10">
          {/* Professional Summary */}
          {convertedData.Summary && (
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-[#2d3748] pb-2 mb-5 border-b-2 border-[#e2e8f0]">
                Professional Summary
              </h2>
              <p className="text-base text-[#718096] leading-relaxed">{convertedData.Summary}</p>
            </section>
          )}

          {/* Work Experience */}
          {convertedData.ProfessionalExperience && convertedData.ProfessionalExperience.length > 0 && (
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-[#2d3748] pb-2 mb-5 border-b-2 border-[#e2e8f0]">
                Work Experience
              </h2>
              {convertedData.ProfessionalExperience.map((job, index) => (
                <div key={index} className="mb-6">
                  <div className="flex justify-between items-baseline mb-1">
                    <h3 className="text-lg font-semibold">{job.JobTitle}</h3>
                    {(job.StartDate || job.EndDate) && (
                      <span className="text-sm italic text-[#718096]">
                        {formatDateRange(job.StartDate, job.EndDate)}
                      </span>
                    )}
                  </div>
                  <h4 className="text-base font-medium text-[#e67e22] mb-2">
                    {job.Company}
                  </h4>
                  {job.Responsibilities && job.Responsibilities.length > 0 && (
                    <ul className="list-disc pl-5 space-y-2">
                      {job.Responsibilities.map((resp, respIndex) => (
                        <li key={respIndex} className="text-sm text-[#718096]">
                          {resp}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </section>
          )}

          {/* Key Projects */}
          {convertedData.KeyProjects && convertedData.KeyProjects.length > 0 && (
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-[#2d3748] pb-2 mb-5 border-b-2 border-[#e2e8f0]">
                Key Projects
              </h2>
              {convertedData.KeyProjects.map((project, index) => (
                <div key={index} className="mb-6">
                  <h3 className="text-lg font-semibold">{project.ProjectName}</h3>
                  {project.Company && (
                    <h4 className="text-base font-medium text-[#e67e22] mb-2">
                      {project.Company}
                    </h4>
                  )}
                  {project.Details && project.Details.length > 0 && (
                    <ul className="list-disc pl-5 space-y-2">
                      {project.Details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="text-sm text-[#718096]">
                          {detail}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </section>
          )}
        </main>
      </div>
    </div>
  );
};
